// Chunked video upload helper with resume-on-disconnect.
//
// Why: Emergent's production Cloudflare proxy rejects request bodies > ~100 MB.
// Splitting the file into ~5 MB chunks bypasses that cap entirely — each chunk
// request stays well under the limit, and the backend reassembles them.
//
// Resume support:
//   The active upload_id is persisted to localStorage keyed by a file signature
//   (name + size + lastModified). If the user refreshes mid-upload or loses
//   their network, the next call detects the matching session, asks the server
//   which chunks it already has, and skips them.
//
// Flow:
//   1. POST /videos/upload/init       → server returns { upload_id, chunk_size }
//      [or resume: GET /videos/upload/status to find already-uploaded indices]
//   2. POST /videos/upload/chunk × N (skipping any already received)
//   3. POST /videos/upload/finalize   → server runs the existing pipeline.

// 2 MB chunks: smaller chunks are far more reliable on flaky mobile networks
// (each POST is short, so a temporarily unstable 4G/5G link is less likely to
// time out mid-request). Trade-off: more round-trips, but each one tiny.
const DEFAULT_CHUNK_SIZE = 2 * 1024 * 1024; // 2 MB
const STORAGE_KEY = 'aivue.chunkedUpload.session';

function fileSignature(file) {
  return `${file.name}::${file.size}::${file.lastModified}`;
}

function loadSavedSession(file) {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const saved = JSON.parse(raw);
    if (!saved || saved.signature !== fileSignature(file)) return null;
    // Drop sessions older than 24h
    if (Date.now() - (saved.created_at || 0) > 24 * 60 * 60 * 1000) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return saved;
  } catch {
    return null;
  }
}

function saveSession(file, uploadId, totalChunks) {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        signature: fileSignature(file),
        upload_id: uploadId,
        total_chunks: totalChunks,
        created_at: Date.now(),
      })
    );
  } catch (err) {
    // localStorage full / disabled — ignore, just no resume on this upload
    console.warn('[chunkedUpload] could not save session for resume:', err?.message || err);
  }
}

function clearSession() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (err) {
    console.warn('[chunkedUpload] could not clear session:', err?.message || err);
  }
}

/**
 * Upload a video in chunks, resuming a prior session for the same file if one exists.
 *
 * @param {object}   params
 * @param {object}   params.axiosInstance   Authenticated axios instance.
 * @param {File}     params.file            The selected video file.
 * @param {object}   params.fields          Form fields for finalize (title, genre, ...).
 * @param {function} [params.onProgress]    (pct: 0-100) byte upload progress.
 * @param {function} [params.onStage]       ('uploading' | 'resuming' | 'finalizing') stage callback.
 * @param {number}   [params._retryCount]   Internal: tracks cross-pod retries.
 * @returns {Promise<object>}                Server response from /finalize.
 */
export async function chunkedVideoUpload({ axiosInstance, file, fields, onProgress, onStage, _retryCount = 0 }) {
  const chunkSize = DEFAULT_CHUNK_SIZE;
  const totalChunks = Math.max(1, Math.ceil(file.size / chunkSize));

  // 0) Check for a prior session for this exact file
  const saved = loadSavedSession(file);
  let uploadId = null;
  let alreadyReceived = new Set();

  if (saved && saved.total_chunks === totalChunks) {
    // Try to resume — ask server which chunks it already has
    try {
      if (onStage) onStage('resuming');
      const statusRes = await axiosInstance.get(
        `/videos/upload/status?upload_id=${encodeURIComponent(saved.upload_id)}`
      );
      const indices = statusRes?.data?.received_indices;
      if (Array.isArray(indices)) {
        uploadId = saved.upload_id;
        alreadyReceived = new Set(indices);
      } else {
        clearSession();
      }
    } catch {
      // Session expired or invalid — start fresh
      clearSession();
    }
  }

  // 1) init (only if no resumable session was found)
  if (!uploadId) {
    if (onStage) onStage('uploading');
    const initParams = new URLSearchParams({
      filename: file.name || 'video.mp4',
      content_type: file.type || 'video/mp4',
      file_size: String(file.size),
      total_chunks: String(totalChunks),
    });
    const initRes = await axiosInstance.post(`/videos/upload/init?${initParams.toString()}`);
    uploadId = initRes?.data?.upload_id;
    if (!uploadId) throw new Error('Server did not return an upload_id.');
    saveSession(file, uploadId, totalChunks);
  } else {
    if (onStage) onStage('uploading');
  }

  // 2) chunks (sequential — keeps memory low + lets us report real progress)
  let bytesSent = alreadyReceived.size * chunkSize;
  // Initial progress reflects what's already on the server
  if (onProgress) onProgress(Math.min(99, Math.round((bytesSent / file.size) * 100)));

  for (let i = 0; i < totalChunks; i++) {
    if (alreadyReceived.has(i)) continue;
    const start = i * chunkSize;
    const end = Math.min(start + chunkSize, file.size);
    const blob = file.slice(start, end);
    const fd = new FormData();
    fd.append('chunk', blob, `chunk-${i}`);
    const params = new URLSearchParams({ upload_id: uploadId, index: String(i) });
    let attempt = 0;
    const MAX_ATTEMPTS = 5;
    while (true) {
      try {
        await axiosInstance.post(`/videos/upload/chunk?${params.toString()}`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 60000, // 60s per chunk — generous for a 2 MB POST
        });
        break;
      } catch (err) {
        attempt += 1;
        if (attempt >= MAX_ATTEMPTS) {
          // Bail out but keep the session so the user can retry later — they
          // won't have to re-upload the chunks that already made it.
          const friendly = new Error(
            'Lost connection while uploading. Your progress is saved — just tap Upload again to resume.'
          );
          friendly.cause = err;
          throw friendly;
        }
        // Exponential backoff with jitter: ~500ms, 1s, 2s, 4s
        const delay = Math.min(4000, 500 * Math.pow(2, attempt - 1))
          + Math.floor(Math.random() * 250);
        await new Promise((r) => setTimeout(r, delay));
      }
    }
    bytesSent += blob.size;
    if (onProgress) onProgress(Math.min(99, Math.round((bytesSent / file.size) * 100)));
  }

  // 3) finalize — fast server response, kicks off background processing
  if (onStage) onStage('finalizing');
  const finalizeParams = new URLSearchParams({
    upload_id: uploadId,
    title: fields.title || '',
    genre: fields.genre || '',
    description: fields.description || '',
    tools_used: fields.tools_used || '',
    style: fields.style || '',
    // Use `||` not `??` here: a captured frame at video.currentTime === 0
    // would otherwise ship as "0" and disable the backend's ffmpeg fallback.
    // Also guard NaN explicitly.
    thumbnail_timestamp: String(
      Number.isFinite(fields.thumbnail_timestamp) && fields.thumbnail_timestamp > 0
        ? fields.thumbnail_timestamp
        : 1.0
    ),
    ai_disclosure: fields.ai_disclosure || '',
  });
  if (fields.seed_creator_id) {
    finalizeParams.set('seed_creator_id', fields.seed_creator_id);
  }
  try {
    // Send the custom thumbnail file (if any) as multipart so the backend
    // can use it directly. Backend handler accepts `thumbnail` UploadFile;
    // if absent, it falls back to server-side ffmpeg extraction at the
    // provided thumbnail_timestamp. Both paths work — this just lets mobile
    // users bypass the iOS Safari canvas-capture issue.
    const finalizeBody = new FormData();
    if (fields.thumbnail_file) {
      finalizeBody.append('thumbnail', fields.thumbnail_file);
    }
    const finalizeRes = await axiosInstance.post(
      `/videos/upload/finalize?${finalizeParams.toString()}`,
      finalizeBody,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000, // finalize itself returns in <200ms now
      }
    );
    if (onProgress) onProgress(100);
    clearSession();
    return finalizeRes.data;
  } catch (err) {
    // 4xx (validation, policy, disclosure) → session is gone, cleanup. Network
    // errors / 5xx → keep the session so the user can retry without re-upload.
    const status = err?.response?.status;
    if (status && status >= 400 && status < 500) {
      clearSession();
    }
    throw err;
  }
}
